import { createContext, useContext, useReducer, ReactNode, useEffect } from 'react';
import { User, Tenant, AppState, NotificationState } from '../types';
import { authApi } from '../api/auth';

// Action types
type AppAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_USER'; payload: User | null }
  | { type: 'SET_TENANT'; payload: Tenant | null }
  | { type: 'SET_AUTHENTICATED'; payload: boolean }
  | { type: 'ADD_NOTIFICATION'; payload: NotificationState }
  | { type: 'REMOVE_NOTIFICATION'; payload: string }
  | { type: 'SET_INITIALIZING'; payload: boolean }
  | { type: 'LOGOUT' };

// Initial state
const initialState: AppState & { notifications: NotificationState[]; isInitializing: boolean } = {
  user: null,
  tenant: null,
  isAuthenticated: false,
  isLoading: false,
  isInitializing: true, // Start as true, will be set to false after token check
  notifications: [],
};

// Reducer
function appReducer(
  state: typeof initialState,
  action: AppAction
): typeof initialState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    
    case 'SET_USER':
      return { 
        ...state, 
        user: action.payload,
        isAuthenticated: !!action.payload,
      };
    
    case 'SET_TENANT':
      return { ...state, tenant: action.payload };
    
    case 'SET_AUTHENTICATED':
      return { ...state, isAuthenticated: action.payload };
    
    case 'SET_INITIALIZING':
      return { ...state, isInitializing: action.payload };
    
    case 'ADD_NOTIFICATION':
      return {
        ...state,
        notifications: [...state.notifications, action.payload],
      };
    
    case 'REMOVE_NOTIFICATION':
      return {
        ...state,
        notifications: state.notifications.filter(n => n.id !== action.payload),
      };
    
    case 'LOGOUT':
      localStorage.removeItem('auth_token');
      return {
        ...initialState,
        isInitializing: false,
        notifications: state.notifications,
      };
    
    default:
      return state;
  }
}

// Context
interface AppContextType {
  state: typeof initialState;
  setLoading: (loading: boolean) => void;
  setUser: (user: User | null) => void;
  setTenant: (tenant: Tenant | null) => void;
  setAuthenticated: (authenticated: boolean) => void;
  addNotification: (notification: Omit<NotificationState, 'id'>) => void;
  removeNotification: (id: string) => void;
  logout: () => void;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

// Provider
interface AppProviderProps {
  children: ReactNode;
}

export function AppProvider({ children }: AppProviderProps) {
  const [state, dispatch] = useReducer(appReducer, initialState);

  // Check for existing token on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('auth_token');
      if (token) {
        try {
          const response = await authApi.getCurrentUser();
          if (response.success && response.data) {
            dispatch({ type: 'SET_USER', payload: response.data as User });
          } else {
            // Invalid token, clear it
            localStorage.removeItem('auth_token');
          }
        } catch (error) {
          // Token expired or invalid, clear it
          localStorage.removeItem('auth_token');
        }
      }
      // Mark initialization as complete
      dispatch({ type: 'SET_INITIALIZING', payload: false });
    };
    
    checkAuth();
  }, []);

  const setLoading = (loading: boolean) => {
    dispatch({ type: 'SET_LOADING', payload: loading });
  };

  const setUser = (user: User | null) => {
    dispatch({ type: 'SET_USER', payload: user });
  };

  const setTenant = (tenant: Tenant | null) => {
    dispatch({ type: 'SET_TENANT', payload: tenant });
  };

  const setAuthenticated = (authenticated: boolean) => {
    dispatch({ type: 'SET_AUTHENTICATED', payload: authenticated });
  };

  const addNotification = (notification: Omit<NotificationState, 'id'>) => {
    const id = Math.random().toString(36).substring(2, 9);
    const fullNotification: NotificationState = {
      ...notification,
      id,
      duration: notification.duration || 5000,
    };
    
    dispatch({ type: 'ADD_NOTIFICATION', payload: fullNotification });

    // Auto-remove after duration
    setTimeout(() => {
      removeNotification(id);
    }, fullNotification.duration);
  };

  const removeNotification = (id: string) => {
    dispatch({ type: 'REMOVE_NOTIFICATION', payload: id });
  };

  const logout = () => {
    dispatch({ type: 'LOGOUT' });
  };

  const value: AppContextType = {
    state,
    setLoading,
    setUser,
    setTenant,
    setAuthenticated,
    addNotification,
    removeNotification,
    logout,
  };

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
}

// Hook
export function useApp() {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
}
